import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type FormEvent } from 'react';
import { transportCalc } from '@app/shared/price-calc';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { HistoryPanel } from '../history/HistoryPanel';
import { SettingsDialog } from '../settings/SettingsDialog';
import { ConflictNotice } from '../ui/ConflictNotice';
import { RowMenu } from '../ui/RowMenu';
import { GeneralError, issueFor, PRICING_KEY, PricingNav, SERVICES_RESOURCE, TRANSPORT_RESOURCE } from './PricingLayout';
import type { Service } from './ServicesPage';
import { leaveGuarded, sameValues, useUnsavedGuard } from '../ui/unsaved';

export type TransportSettings = { localKm: number; roundTrip: boolean; version: number };
/** The distance of the worked example next to each transport. */
const EXAMPLE_KM = 120;
const SETTINGS_KEY = [...PRICING_KEY, 'transport-settings'] as const;

type Draft = { price: string; base: string; perKm: string };
const draftOf = (s: Service): Draft => ({
  price: s.priceGrosze !== undefined ? plnToInput(s.priceGrosze) : '',
  base: s.transportBaseGrosze !== undefined ? plnToInput(s.transportBaseGrosze) : '',
  perKm: s.transportPerKmGrosze !== undefined ? plnToInput(s.transportPerKmGrosze) : '',
});
const FIELDS = { price: 'priceGrosze', base: 'transportBaseGrosze', perKm: 'transportPerKmGrosze' } as const;
const LABELS = { price: 'Ryczałt (zł)', base: 'Kwota bazowa (zł)', perKm: 'Stawka za km (zł)' } as const;

/** What the role may see of the amounts (rule 7a: hidden fields are neither shown nor sent). */
type Visible = { price: boolean; base: boolean; perKm: boolean };

/** Edit the amounts of one transport; only the visible and changed ones are sent. */
function TransportForm({ service, visible, onClose }: { service: Service; visible: Visible; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [base, setBase] = useState(service);
  const [draft, setDraft] = useState(draftOf(service));
  const [localError, setLocalError] = useState<Partial<Record<keyof Draft, string>>>({});
  const save = useMutation({
    mutationFn: (body: Record<string, number>) => api<Service>(`/api/pricing/services/${base.id}`, { method: 'PATCH', body: { version: base.version, ...body } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRICING_KEY });
      onClose();
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;

  function reload() {
    const current = (save.error as ApiError).current as Service | undefined;
    if (current) {
      setBase(current);
      setDraft(draftOf(current));
    }
    save.reset();
    void queryClient.invalidateQueries({ queryKey: PRICING_KEY });
  }

  /** What changed, or null with the reasons shown. */
  function changes(): Record<string, number> | null {
    const errors: Partial<Record<keyof Draft, string>> = {};
    const body: Record<string, number> = {};
    for (const key of Object.keys(FIELDS) as (keyof Draft)[]) {
      if (!visible[key]) continue;
      try {
        const grosze = parsePln(draft[key]);
        if (grosze !== base[FIELDS[key]]) body[FIELDS[key]] = grosze;
      } catch (err) {
        errors[key] = (err as Error).message;
      }
    }
    setLocalError(errors);
    return Object.keys(errors).length > 0 ? null : body;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = changes();
    if (!body) return;
    if (Object.keys(body).length === 0) onClose();
    else save.mutate(body);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(draft, draftOf(base)),
    save: async () => {
      const body = changes();
      if (!body) return false;
      if (Object.keys(body).length > 0) await save.mutateAsync(body);
      return true;
    },
  });

  return (
    <form ref={guardRef} className="panel form transport-form" aria-label={`Edycja: ${base.name}`} data-feature-id="pricing.transport" onSubmit={submit}>
      {conflict && <ConflictNotice onReload={reload} />}
      <GeneralError error={save.error} fields={Object.values(FIELDS)} />
      <div className="transport-form-fields">
        {(Object.keys(FIELDS) as (keyof Draft)[])
          .filter((key) => visible[key])
          .map((key) => {
            const message = localError[key] ?? issueFor(save.error, FIELDS[key]);
            return (
              <label key={key} className="field">
                <span>{LABELS[key]}</span>
                <input inputMode="decimal" value={draft[key]} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} autoComplete="off" />
                {message && <span className="field-error">{message}</span>}
              </label>
            );
          })}
      </div>
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending}>
          {save.isPending ? 'Zapisywanie…' : 'Zapisz zmiany'}
        </button>
        <button className="btn" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/** The gear's dialog: up to how many km the flat amount applies, and whether km count there and back. */
function TransportSettingsForm({ settings, canEdit, onClose }: { settings: TransportSettings; canEdit: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [base, setBase] = useState(settings);
  const [km, setKm] = useState(String(settings.localKm));
  const [roundTrip, setRoundTrip] = useState(settings.roundTrip);
  const [localError, setLocalError] = useState('');
  const save = useMutation({
    mutationFn: (body: Partial<TransportSettings>) => api<TransportSettings>('/api/pricing/transport-settings', { method: 'PATCH', body: { version: base.version, ...body } }),
    onSuccess: async (fresh) => {
      queryClient.setQueryData(SETTINGS_KEY, fresh);
      await queryClient.invalidateQueries({ queryKey: PRICING_KEY });
      onClose();
    },
  });
  const conflict = save.error instanceof ApiError && save.error.status === 409;

  function settingsBody(): Partial<TransportSettings> | null {
    if (!/^\d{1,6}$/.test(km.trim())) {
      setLocalError('Podaj liczbę całkowitą km, np. 50');
      return null;
    }
    setLocalError('');
    return { ...(Number(km) !== base.localKm ? { localKm: Number(km) } : {}), ...(roundTrip !== base.roundTrip ? { roundTrip } : {}) };
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = settingsBody();
    if (!body) return;
    if (Object.keys(body).length === 0) onClose();
    else save.mutate(body);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: canEdit && (km.trim() !== String(base.localKm) || roundTrip !== base.roundTrip),
    save: async () => {
      const body = settingsBody();
      if (!body) return false;
      if (Object.keys(body).length > 0) await save.mutateAsync(body);
      return true;
    },
  });

  return (
    <form ref={guardRef} className="form transport-settings" aria-label="Ustawienia transportu" data-feature-id="pricing.transport" onSubmit={submit}>
      {conflict && (
        <ConflictNotice
          onReload={() => {
            const current = (save.error as ApiError).current as TransportSettings | undefined;
            if (current) {
              setBase(current);
              setKm(String(current.localKm));
              setRoundTrip(current.roundTrip);
            }
            save.reset();
          }}
        />
      )}
      <GeneralError error={save.error} fields={['localKm', 'roundTrip']} />
      <div className="field">
        <label htmlFor="transport-local-km">Ryczałt do (km)</label>
        <input id="transport-local-km" inputMode="numeric" value={km} disabled={!canEdit} onChange={(e) => setKm(e.target.value)} autoComplete="off" aria-describedby="transport-local-km-hint" />
        <span className="hint" id="transport-local-km-hint">
          Do tej odległości od magazynu transport kosztuje ryczałt (np. Trójmiasto: 50 km).
        </span>
        {(localError || issueFor(save.error, 'localKm')) && <span className="field-error">{localError || issueFor(save.error, 'localKm')}</span>}
      </div>
      <label className="check">
        <input type="checkbox" checked={roundTrip} disabled={!canEdit} onChange={(e) => setRoundTrip(e.target.checked)} />
        <span>Km liczone tam i z powrotem (120 km od magazynu = 240 km)</span>
      </label>
      {canEdit && (
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
          </button>
        </div>
      )}
    </form>
  );
}

/** "np. 120 km: 1 020,00 zł" — the same calculation the quote uses. */
function example(s: Service, settings: TransportSettings | undefined): string | null {
  if (!settings || s.priceGrosze === undefined || s.transportBaseGrosze === undefined || s.transportPerKmGrosze === undefined) return null;
  try {
    const calc = transportCalc(
      { flatGrosze: s.priceGrosze, baseGrosze: s.transportBaseGrosze, perKmGrosze: s.transportPerKmGrosze, localKm: settings.localKm, roundTrip: settings.roundTrip },
      EXAMPLE_KM,
    );
    return `np. ${EXAMPLE_KM} km: ${formatPln(calc.grosze)}`;
  } catch {
    return null;
  }
}

/** "Cenniki → Transport": the two transports (owner remarks 3/22, 3/23) and, behind the gear, how km are counted. */
export function TransportPage() {
  const { me } = useMe();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const list = useQuery({ queryKey: [...PRICING_KEY, 'services'], queryFn: () => api<{ services: Service[] }>('/api/pricing/services') });
  const seeSettings = !!me && can(me, TRANSPORT_RESOURCE, 'view');
  const settings = useQuery({ queryKey: SETTINGS_KEY, queryFn: () => api<TransportSettings>('/api/pricing/transport-settings'), enabled: seeSettings });
  if (!me) return null;
  const transports = (list.data?.services ?? []).filter((s) => s.transport);
  const visible: Visible = {
    price: fieldVisible(me, SERVICES_RESOURCE, 'priceGrosze'),
    base: fieldVisible(me, SERVICES_RESOURCE, 'transportBaseGrosze'),
    perKm: fieldVisible(me, SERVICES_RESOURCE, 'transportPerKmGrosze'),
  };
  const seeHistory = can(me, 'history.entries', 'view');
  const columns = 2 + Number(visible.price) + Number(visible.base) + Number(visible.perKm);
  const s = settings.data;
  const flatHead = s ? `Ryczałt do ${s.localKm} km` : 'Ryczałt';

  return (
    <section className="page" data-feature-id="pricing.transport">
      <div className="page-head">
        <h2>Cenniki</h2>
      </div>
      <PricingNav me={me} />
      <p className="muted">
        Transport ciężarowy i osobowy to dwie pozycje kosztorysu. Blisko magazynu kosztują ryczałt, dalej kwotę bazową plus stawkę za każdy km. Odległość bierze się z trasy do miejsca wydarzenia
        („ok. N km od magazynu”).
      </p>

      <div className="section-head transport-head">
        <p className="transport-rule">
          {s ? (
            <>
              <strong>Ryczałt do {s.localKm} km</strong> od magazynu · dalej kwota bazowa + km × stawka, km liczone {s.roundTrip ? <strong>tam i z powrotem</strong> : <strong>w jedną stronę</strong>}
            </>
          ) : (
            'Ryczałt blisko magazynu, dalej kwota bazowa + km × stawka.'
          )}
        </p>
        {seeSettings && <RowMenu icon="gear" label="Ustawienia transportu" featureId="pricing.transport" actions={[{ label: 'Próg km i sposób liczenia…', onSelect: () => setSettingsOpen(true) }]} />}
      </div>

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać cennika transportu'}</p>}
      {list.data && transports.length === 0 && <p className="muted">Brak pozycji transportu w cenniku.</p>}
      {transports.length > 0 && (
        <div className="table-wrap">
          <table className="table pricing-table" aria-label="Transport">
            <thead>
              <tr>
                <th scope="col">Rodzaj</th>
                {visible.price && (
                  <th scope="col" className="num">
                    {flatHead}
                  </th>
                )}
                {visible.base && (
                  <th scope="col" className="num hide-phone">
                    Kwota bazowa
                  </th>
                )}
                {visible.perKm && (
                  <th scope="col" className="num">
                    Stawka za km
                  </th>
                )}
                <th scope="col" className="tpl-actions-col">
                  <span className="visually-hidden">Działania</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {transports.map((t) => {
                const hint = example(t, s);
                const more = seeHistory ? [{ label: historyId === t.id ? 'Ukryj historię zmian' : 'Historia zmian', onSelect: () => setHistoryId(historyId === t.id ? null : t.id) }] : [];
                return (
                  <Fragment key={t.id}>
                    <tr>
                      <td>
                        <strong>{t.name}</strong>
                        {hint && <span className="muted transport-example">{hint}</span>}
                      </td>
                      {visible.price && <td className="num">{t.priceGrosze !== undefined ? formatPln(t.priceGrosze) : ''}</td>}
                      {visible.base && <td className="num hide-phone">{t.transportBaseGrosze !== undefined ? formatPln(t.transportBaseGrosze) : ''}</td>}
                      {visible.perKm && <td className="num">{t.transportPerKmGrosze !== undefined ? formatPln(t.transportPerKmGrosze) : ''}</td>}
                      <td className="tpl-actions">
                        {can(me, SERVICES_RESOURCE, 'edit') && editingId !== t.id && (
                          <button className="btn small" type="button" aria-label={`Edytuj: ${t.name}`} onClick={() => leaveGuarded(() => setEditingId(t.id))}>
                            Edytuj
                          </button>
                        )}
                        {more.length > 0 && <RowMenu label={`Więcej działań: ${t.name}`} actions={more} />}
                      </td>
                    </tr>
                    {editingId === t.id && (
                      <tr className="row-editor">
                        <td colSpan={columns}>
                          <TransportForm service={t} visible={visible} onClose={() => setEditingId(null)} />
                        </td>
                      </tr>
                    )}
                    {historyId === t.id && seeHistory && (
                      <tr>
                        <td colSpan={columns}>
                          <HistoryPanel entityId={t.id} resource={SERVICES_RESOURCE} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {settingsOpen && s && (
        <SettingsDialog title="Ustawienia transportu" onClose={() => setSettingsOpen(false)} allSettingsLink={false}>
          <TransportSettingsForm settings={s} canEdit={can(me, TRANSPORT_RESOURCE, 'edit')} onClose={() => setSettingsOpen(false)} />
        </SettingsDialog>
      )}
    </section>
  );
}
